import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import {
  addDays,
  compareAsc,
  differenceInCalendarDays,
  format,
  getDate,
  getMonth,
  getYear,
  isValid,
} from "date-fns";
import { decamelizeKeys } from "humps";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import { HotelCheckIn } from "admin/types/hotelCheckIn";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { RootState } from "admin/store";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { Meta } from "admin/types/meta";
import { parseDate } from "admin/utils/dateUtil";
import { clone } from "admin/utils/objectUtil";

const key = "hotelCheckIn";
const adapter = createEntityAdapter<HotelCheckIn.CheckIn>();

const initialStateValues: HotelCheckIn.HotelCheckInState = {
  hotelCheckIn: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async * */
export const fetchHotelCheckIns = createAsyncThunk<
  {
    hotelCheckIns: HotelCheckIn.CheckIn[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelCheckIns`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, param, headers, isServerSide, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/check_ins.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/check_ins.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotelCheckIns: result.data.checkIns,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchHotelCheckIn = createAsyncThunk<
  {
    hotelCheckIn: HotelCheckIn.CheckIn;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    id: number;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelCheckIn`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, headers, isServerSide, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/check_ins/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/check_ins/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotelCheckIn: result.data.checkIn,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelCheckIn = createAsyncThunk<
  { hotelCheckIn: HotelCheckIn.CheckIn },
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelCheckIn`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/check_ins.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/check_ins.json`;
    const result = await createWrapper().post(url, {
      check_in: params,
    });
    return { hotelCheckIn: result.data.checkIn };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelCheckIn = createAsyncThunk<
  { hotelCheckIn: HotelCheckIn.CheckIn },
  {
    context: PageContext.PageContext;
    hotelId: string;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelCheckIn`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/check_ins/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/check_ins/${id}.json`;
    const result = await createWrapper().put(url, {
      check_in: params,
    });
    return { hotelCheckIn: result.data.checkIn };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchHotelCheckInForm = createAsyncThunk<
  {
    hotelCheckInForm: HotelCheckIn.CheckInForm;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelCheckInForm`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, headers, isServerSide, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/check_in_form.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/check_in_form.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotelCheckInForm: result.data.checkInForm,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const hotelCheckInSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    clearHotelCheckIn: (state) => {
      state.hotelCheckIn = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelCheckIn };
      })
      .addCase(fetchHotelCheckIns.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.hotelCheckIns);
        state.meta = action.payload.meta;
      })
      .addCase(fetchHotelCheckIn.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.hotelCheckIn);
        state.hotelCheckIn = action.payload.hotelCheckIn;
      })
      .addCase(postHotelCheckIn.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.hotelCheckIn);
        state.hotelCheckIn = action.payload.hotelCheckIn;
      })
      .addCase(putHotelCheckIn.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.hotelCheckIn);
        state.hotelCheckIn = action.payload.hotelCheckIn;
      })
      .addCase(fetchHotelCheckInForm.fulfilled, (state, action) => {
        state.hotelCheckInForm = action.payload.hotelCheckInForm;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload?.error_messages;
        state.loading = false;
      });
  },
});

/** selector **/
const stateSelector = (state: RootState) => state[key];

export const {
  selectAll: hotelCheckInsSelector,
  selectById: hotelCheckInByIdSelector,
} = adapter.getSelectors((state: RootState) => state.hotelCheckIn);

export const hotelCheckInSelector = createSelector(
  stateSelector,
  (state) => state.hotelCheckIn,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const hotelCheckInsMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const hotelCheckInFormSelector = createSelector(
  stateSelector,
  (state) => state.hotelCheckInForm,
);

/** action export **/
export const { clearHotelCheckIn } = hotelCheckInSlice.actions;

const convertCheckInGuestInfoToParams = (
  guestInfo: any,
  index: number,
  bookingRoomCode?: string,
) => {
  const params = { ...guestInfo };
  if (
    params.date_of_birth_year &&
    params.date_of_birth_month &&
    params.date_of_birth_day
  ) {
    const month = String(params.date_of_birth_month).padStart(2, "0");
    const day = String(params.date_of_birth_day).padStart(2, "0");
    params.birthday = `${params.date_of_birth_year}-${month}-${day}`;
  }

  if (bookingRoomCode) {
    params.booking_room_code = bookingRoomCode;
  }
  params.booking_room_index = index;

  delete params.date_of_birth_year;
  delete params.date_of_birth_month;
  delete params.date_of_birth_day;
  delete params.is_not_living_in_japan;
  delete params.is_different_address;
  return params;
};

export const convertCheckInFormValuesToParams = (data: any) => {
  const params = { ...data };
  const checkInDateString = params.checkin_date.replace(/\//g, "-");
  params.checkin_date = checkInDateString;
  const checkInDate = parseDate(checkInDateString);
  params.checkout_date = format(
    addDays(checkInDate, Number(params.stay_num)),
    "yyyy-LL-dd",
  );
  delete params.stay_num;
  delete params.total_rooms;

  params.check_in_guest_infos = params.check_in_rooms.flatMap(
    (checkInRoom, index) => {
      const bookingRoomCode = checkInRoom.booking_room_code;
      return checkInRoom.guests.map((guest) => {
        return convertCheckInGuestInfoToParams(guest, index, bookingRoomCode);
      });
    },
  );
  delete params.check_in_rooms;

  return params;
};

export const groupCheckInGuestInfos = (checkInGuestInfos: any) => {
  return checkInGuestInfos.reduce((acc, guest) => {
    const { bookingRoomCode, bookingRoomIndex, ...rest } = guest;
    if (bookingRoomCode) {
      if (!acc[bookingRoomCode]) {
        acc[bookingRoomCode] = {
          bookingRoomCode: bookingRoomCode,
          guests: [],
        };
      }
      acc[bookingRoomCode].guests.push(rest);
    } else if (bookingRoomIndex !== undefined && bookingRoomIndex !== null) {
      if (!acc[bookingRoomIndex]) {
        acc[bookingRoomIndex] = {
          bookingRoomIndex: bookingRoomIndex,
          guests: [],
        };
      }
      acc[bookingRoomIndex].guests.push(rest);
    }
    return acc;
  }, {});
};

export const convertHotelCheckInToFormValues = (data: any) => {
  const values = { ...data };

  const representativeGuestInfos = values.checkInGuestInfos.filter(
    (guestInfo) => {
      return guestInfo.representative;
    },
  );

  const checkInGuestInfos = values.checkInGuestInfos.map((guestInfo) => {
    const guestValues = { ...guestInfo };
    if (guestValues.birthday) {
      const birthdayDate = parseDate(guestValues.birthday);
      if (isValid(birthdayDate)) {
        guestValues.date_of_birth_year = getYear(birthdayDate);
        guestValues.date_of_birth_month = getMonth(birthdayDate) + 1;
        guestValues.date_of_birth_day = getDate(birthdayDate);
      }
      delete guestValues.birthday;
    }

    const isNotLivingInJapan = !!guestValues.passportNumber;
    if (isNotLivingInJapan) {
      guestValues.is_not_living_in_japan = true;
    }
    if (!guestValues.representative) {
      const targetRepresentative = representativeGuestInfos.find(
        (representativeGuestInfo) => {
          if (
            guestValues.bookingRoomCode &&
            guestValues.bookingRoomCode ===
              representativeGuestInfo.bookingRoomCode
          ) {
            return true;
          } else if (
            guestValues.bookingRoomIndex ===
            representativeGuestInfo.bookingRoomIndex
          ) {
            return true;
          }
          return false;
        },
      );
      if (
        targetRepresentative.adress !== guestValues.address &&
        (targetRepresentative.countryCode !== guestValues.countryCode ||
          targetRepresentative.japanPrefectureId !==
            guestValues.japanPrefectureId)
      ) {
        guestValues.is_different_address = true;
      }
    }
    delete guestValues.japanPrefecture;
    return guestValues;
  });

  const rooms = groupCheckInGuestInfos(checkInGuestInfos);
  const keys = Object.keys(rooms);
  values.checkInRooms = keys.map((key) => {
    return { ...rooms[key], guest_num: rooms[key].guests.length };
  });
  delete values.checkInGuestInfos;

  values.total_rooms = values.checkInRooms.length || 1;

  const checkinDate = parseDate(values.checkinDate);
  const checkoutDate = parseDate(values.checkoutDate);
  if (
    isValid(checkinDate) &&
    isValid(checkoutDate) &&
    compareAsc(checkoutDate, checkinDate)
  ) {
    values.stayNum = differenceInCalendarDays(checkoutDate, checkinDate);
  }

  return values;
};

export const convertCheckInDefaultValuesToFormValues = (
  defaultValues: any[],
) => {
  const values = clone(decamelizeKeys(defaultValues));
  const today = new Date();
  const todayString = format(today, "yyyy-LL-dd");

  if (!values || values.length === 0) {
    return {
      checkin_date: todayString,
      total_rooms: 1,
      check_in_rooms: [{ guests: [{ representative: true }] }],
    };
  }

  const checkInRooms = clone(values);
  checkInRooms.map((checkInRoom) => {
    delete checkInRoom.checkin_date;
    delete checkInRoom.stays;
    return checkInRoom;
  });

  // 現在チェックイン日と宿泊数は全ての部屋で同じ前提
  const firstRoom = values[0];
  const checkinDate = firstRoom.checkin_date || todayString;
  const stayNum = firstRoom.stays || 1;

  return {
    checkin_date: checkinDate,
    stay_num: stayNum,
    total_rooms: values.length,
    check_in_rooms: checkInRooms,
  };
};
